import { TrackingPageSettings } from "@redotech/order-tracking-common/tracking-page-definition";
import { memo } from "react";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import { TrackingPageEnvironment } from "../../context/tracking-page-context";
import { getBlockStyles, getScrollbarHiddenStyles } from "./block-styles";
import { OverflowScrollArea } from "./overflow-scroll-area";

export interface BlockOverflowCue {
  scrollbar: boolean;
  fade: boolean;
  scrollbarColor?: string;
}

interface BlockWrapperProps {
  pageSettings: TrackingPageSettings | undefined;
  environment: TrackingPageEnvironment;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  withBorder?: boolean;
  withScrollbar?: boolean;
  /**
   * With `withScrollbar`, moves scrolling into an inner area that draws its
   * own always-visible bar and/or edge fade while the content overflows.
   * Without it the block scrolls itself with the native bar hidden.
   */
  overflowCue?: BlockOverflowCue;
  transparentBackground?: boolean;
  inert?: boolean;
}

/**
 * Common wrapper for tracking page blocks with consistent styling
 */
export const BlockWrapper = memo(function BlockWrapper({
  pageSettings,
  environment,
  loading = false,
  children,
  className,
  withBorder = true,
  withScrollbar = false,
  overflowCue,
  transparentBackground = false,
  inert,
}: BlockWrapperProps) {
  const baseStyles = getBlockStyles(pageSettings);
  const cueScrollArea = withScrollbar && overflowCue !== undefined;
  const scrollStyles =
    withScrollbar && !cueScrollArea ? getScrollbarHiddenStyles() : {};
  const finalStyles = transparentBackground
    ? { ...baseStyles, backgroundColor: "transparent" }
    : baseStyles;

  const borderStyle = withBorder
    ? { border: `1px solid ${baseStyles.borderColor ?? "#d6d6d6"}` }
    : {};

  if (loading) {
    return (
      <Flex
        align="center"
        className={className}
        dir="column"
        flex={1}
        gap="lg"
        justify="center"
        p="3xl"
        style={{ ...finalStyles, ...borderStyle }}
      >
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  return (
    <Flex
      className={className}
      dir="column"
      flex={1}
      gap="xl"
      inert={inert ?? environment === TrackingPageEnvironment.BUILDER}
      overflow="hidden"
      style={{ ...finalStyles, ...scrollStyles, ...borderStyle }}
    >
      {cueScrollArea ? (
        <OverflowScrollArea
          fade={overflowCue.fade}
          scrollbar={overflowCue.scrollbar}
          scrollbarColor={overflowCue.scrollbarColor}
        >
          {children}
        </OverflowScrollArea>
      ) : (
        children
      )}
    </Flex>
  );
});
